import React from 'react';
import PropTypes from 'prop-types';
import { Icon, Loading, Tooltip } from '@folio/stripes/components';
import { FormattedMessage, FormattedRelativeTime, useIntl } from 'react-intl';
import { EXEC_STAGE } from '../../hooks/useLastExecution';

const BADGE_STYLES = {
  success: { background: '#d1e7dd', color: '#0a3622', border: '1px solid #a3cfbb' },
  failed:  { background: '#f8d7da', color: '#58151c', border: '1px solid #f1aeb5' },
  timeout: { background: '#fff3cd', color: '#664d03', border: '1px solid #ffda6a' },
};

const StatusSpan = ({ style, onClick, children }) => (
  <span
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
    style={{
      display:      'inline-flex',
      alignItems:   'center',
      gap:          '0.25rem',
      padding:      '0.2em 0.5em',
      borderRadius: '0.25rem',
      fontSize:     '0.8rem',
      fontWeight:   '600',
      cursor:       onClick ? 'pointer' : 'default',
      whiteSpace:   'nowrap',
      ...style,
    }}
  >
    {children}
  </span>
);

function LastRunBadge({ stage, execution, elapsed, onClick }) {
  const intl = useIntl();
  const tooltipId = `last-run-badge-tooltip-${execution?.id || 'none'}`;

  const renderBadge = () => {
    switch (stage) {
      case EXEC_STAGE.RUNNING:
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
            <Loading size="small" />
            <span style={{ fontSize: '0.8rem', color: '#666' }}>
              <FormattedMessage
                id="ui-reports.lastRun.running"
                defaultMessage="Running... {s}s"
                values={{ s: elapsed }}
              />
            </span>
          </span>
        );

      case EXEC_STAGE.SUCCESS:
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
            <StatusSpan style={BADGE_STYLES.success} onClick={onClick}>
              <Icon icon="check-circle" size="small" />
              <FormattedMessage id="ui-reports.lastRun.success" defaultMessage="Success" />
            </StatusSpan>
            {execution?.executionDate && (
              <span style={{ fontSize: '0.75rem', color: '#888' }}>
                <FormattedRelativeTime
                  value={(new Date(execution.executionDate) - Date.now()) / 1000}
                  numeric="auto"
                  updateIntervalInSeconds={60}
                />
              </span>
            )}
          </span>
        );

      case EXEC_STAGE.FAILED:
        return (
          <StatusSpan style={BADGE_STYLES.failed} onClick={onClick}>
            <Icon icon="times-circle" size="small" />
            <FormattedMessage id="ui-reports.lastRun.failed" defaultMessage="Failed" />
          </StatusSpan>
        );

      case EXEC_STAGE.TIMEOUT:
        return (
          <StatusSpan style={BADGE_STYLES.timeout} onClick={onClick}>
            <Icon icon="clock" size="small" />
            <FormattedMessage id="ui-reports.lastRun.timeout" defaultMessage="Timeout" />
          </StatusSpan>
        );

      default:
        return (
          <span style={{ fontSize: '0.8rem', color: '#aaa' }}>—</span>
        );
    }
  };

  const badge = renderBadge();

  if (stage === EXEC_STAGE.IDLE || stage === EXEC_STAGE.RUNNING) return badge;

  return (
    <Tooltip
      id={tooltipId}
      text={
        stage === EXEC_STAGE.SUCCESS
          ? intl.formatMessage(
              { id: 'ui-reports.lastRun.tooltip.success', defaultMessage: '{count} records — click to view history' },
              { count: execution?.recordsCount ?? '?' },
            )
          : intl.formatMessage(
              { id: 'ui-reports.lastRun.tooltip.failed', defaultMessage: 'Click to view error details' },
            )
      }
    >
      {({ ref, ariaIds }) => (
        <span ref={ref} aria-describedby={ariaIds.text}>
          {badge}
        </span>
      )}
    </Tooltip>
  );
}

LastRunBadge.propTypes = {
  stage:     PropTypes.string.isRequired,
  execution: PropTypes.object,
  elapsed:   PropTypes.number,
  onClick:   PropTypes.func,
};

LastRunBadge.defaultProps = { elapsed: 0 };

export default LastRunBadge;
